import type { DeviceStatus, NetworkQualityStatus, NetworkQuality, ConnectionState, LocationType } from '../api/client'

// Status indicator component with LED-like glow effect
export function StatusIndicator({ status, size = 'md', pulse = false }: {
  status: DeviceStatus,
  size?: 'sm' | 'md' | 'lg',
  pulse?: boolean
}) {
  const colors = {
    healthy: 'bg-emerald-400 shadow-emerald-400/60',
    warning: 'bg-amber-400 shadow-amber-400/60',
    critical: 'bg-red-400 shadow-red-400/60',
  }

  const sizes = {
    sm: 'w-2 h-2 shadow-[0_0_6px]',
    md: 'w-3 h-3 shadow-[0_0_8px]',
    lg: 'w-4 h-4 shadow-[0_0_12px]',
  }

  return (
    <span className={`
      inline-block rounded-full ${colors[status]} ${sizes[size]}
      ${pulse && status === 'critical' ? 'animate-pulse' : ''}
    `} />
  )
}

// Network quality indicator (signal bars style)
export function NetworkQualityIndicator({ status, size = 'md' }: {
  status: NetworkQualityStatus | null,
  size?: 'sm' | 'md'
}) {
  if (!status) {
    // Offline - show gray bars
    return (
      <div className={`flex items-end gap-0.5 ${size === 'sm' ? 'h-3' : 'h-4'}`}>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className="bg-slate-600 rounded-sm"
            style={{
              width: size === 'sm' ? 3 : 4,
              height: `${(i / 3) * 100}%`,
            }}
          />
        ))}
      </div>
    )
  }

  const colors = {
    good: 'bg-emerald-400',
    degraded: 'bg-amber-400',
    poor: 'bg-red-400',
    offline: 'bg-slate-600',
  }

  const barsLit = {
    good: 3,
    degraded: 2,
    poor: 1,
    offline: 0,
  }

  return (
    <div className={`flex items-end gap-0.5 ${size === 'sm' ? 'h-3' : 'h-4'}`} title={`Network: ${status}`}>
      {[1, 2, 3].map(i => (
        <div
          key={i}
          className={`rounded-sm ${i <= barsLit[status] ? colors[status] : 'bg-slate-700'}`}
          style={{
            width: size === 'sm' ? 3 : 4,
            height: `${(i / 3) * 100}%`,
          }}
        />
      ))}
    </div>
  )
}

export function NetworkQualityBadge({ quality }: { quality: NetworkQuality | null }) {
  if (!quality) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-xs">
        <NetworkQualityIndicator status={null} size="sm" />
        <span>Offline</span>
      </div>
    )
  }

  const statusLabels = {
    good: 'Good',
    degraded: 'Degraded',
    poor: 'Poor',
    offline: 'Offline',
  }

  const statusColors = {
    good: 'text-emerald-400',
    degraded: 'text-amber-400',
    poor: 'text-red-400',
    offline: 'text-slate-500',
  }

  return (
    <div className="flex items-center gap-2">
      <NetworkQualityIndicator status={quality.status} size="sm" />
      <div className="text-xs">
        <span className={statusColors[quality.status]}>{statusLabels[quality.status]}</span>
        <span className="text-slate-500 ml-1.5">
          {quality.latencyMs}ms / {quality.lossPercent}% / {quality.jitterMs}ms
        </span>
      </div>
    </div>
  )
}

// Location badge component
export function LocationBadge({ type, officeName, compact = false }: {
  type: LocationType,
  officeName?: string,
  compact?: boolean
}) {
  const config = {
    office: {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
        </svg>
      ),
      label: officeName || 'Office',
      color: 'text-blue-400 bg-blue-500/10',
    },
    home: {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        </svg>
      ),
      label: 'Home',
      color: 'text-purple-400 bg-purple-500/10',
    },
    remote: {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
        </svg>
      ),
      label: 'Remote',
      color: 'text-amber-400 bg-amber-500/10',
    },
    unknown: {
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
        </svg>
      ),
      label: 'Unknown',
      color: 'text-slate-400 bg-slate-500/10',
    },
  }

  const { icon, label, color } = config[type]

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 ${color} rounded px-1.5 py-0.5`} title={label}>
        {icon}
      </span>
    )
  }

  return (
    <span className={`inline-flex items-center gap-1.5 ${color} rounded-md px-2 py-1 text-xs`}>
      {icon}
      <span>{label}</span>
    </span>
  )
}

// Connection state indicator
export function ConnectionStateIndicator({ state, size = 'md' }: {
  state: ConnectionState,
  size?: 'sm' | 'md'
}) {
  const config = {
    streaming: {
      icon: (
        <svg className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0" />
        </svg>
      ),
      color: 'text-emerald-400',
      label: 'Streaming',
    },
    idle: {
      icon: (
        <svg className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      ),
      color: 'text-amber-400',
      label: 'Idle',
    },
    offline: {
      icon: (
        <svg className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      ),
      color: 'text-slate-500',
      label: 'Offline',
    },
  }

  const { icon, color, label } = config[state]

  return (
    <span className={`inline-flex items-center ${color}`} title={label}>
      {icon}
    </span>
  )
}

// Stat badge component
export function StatBadge({ count, status, label }: { count: number, status: DeviceStatus, label: string }) {
  if (count === 0) return null

  return (
    <div className="flex items-center gap-1.5">
      <StatusIndicator status={status} size="sm" />
      <span className="text-slate-300 text-sm font-mono">{count}</span>
      <span className="text-slate-500 text-xs">{label}</span>
    </div>
  )
}

// Info row for detail views
export function InfoRow({ label, value, mono = false, highlight = false }: {
  label: string,
  value: string | number,
  mono?: boolean,
  highlight?: boolean
}) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-800/50">
      <dt className="text-slate-500 text-sm">{label}</dt>
      <dd className={`
        text-sm
        ${mono ? 'font-mono' : ''}
        ${highlight ? 'text-amber-400' : 'text-white'}
      `}>
        {value}
      </dd>
    </div>
  )
}

// Progress bar for percentage values (CPU, memory, disk)
export function ProgressBar({ value, label, size = 'md' }: {
  value: number,
  label?: string,
  size?: 'sm' | 'md'
}) {
  const pct = Math.min(100, Math.max(0, value))
  const color = pct > 90 ? 'bg-red-400' : pct > 70 ? 'bg-amber-400' : 'bg-emerald-400'

  return (
    <div className="flex items-center gap-3">
      {label && <span className="text-slate-500 text-xs w-16 shrink-0">{label}</span>}
      <div className={`flex-1 bg-slate-700/50 rounded-full overflow-hidden ${size === 'sm' ? 'h-1.5' : 'h-2'}`}>
        <div className={`${color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-slate-300 text-xs font-mono w-10 text-right">{Math.round(pct)}%</span>
    </div>
  )
}

// Severity badge for issues
export function SeverityBadge({ severity }: { severity: string }) {
  const config: Record<string, { bg: string, text: string }> = {
    critical: { bg: 'bg-red-500/15 border-red-500/30', text: 'text-red-400' },
    warning: { bg: 'bg-amber-500/15 border-amber-500/30', text: 'text-amber-400' },
    info: { bg: 'bg-blue-500/15 border-blue-500/30', text: 'text-blue-400' },
  }
  const style = config[severity] ?? config.info

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${style.bg} ${style.text}`}>
      {severity}
    </span>
  )
}

// Simple sparkline chart component
export function Sparkline({ data, color, height = 40 }: { data: number[], color: string, height?: number }) {
  if (data.length === 0) return null

  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100
    const y = height - ((value - min) / range) * (height - 4)
    return `${x},${y}`
  }).join(' ')

  return (
    <svg className="w-full" height={height} preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  )
}

// Summary card component
export function SummaryCard({ label, value, icon, color }: {
  label: string,
  value: number,
  icon: string,
  color?: 'emerald' | 'amber' | 'red'
}) {
  const colorClasses = {
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
  }

  const iconPaths = {
    devices: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
    check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    alert: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  }

  return (
    <div className={`
      rounded-xl p-4 border backdrop-blur-sm
      ${color ? colorClasses[color] : 'bg-slate-900/50 border-slate-800 text-slate-400'}
    `}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 font-mono ${color ? '' : 'text-white'}`}>
            {value}
          </p>
        </div>
        <svg className="w-8 h-8 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[icon as keyof typeof iconPaths]} />
        </svg>
      </div>
    </div>
  )
}

// Navigation item component
export function NavItem({ icon, label, active = false, badge, onClick }: {
  icon: string,
  label: string,
  active?: boolean,
  badge?: number,
  onClick?: () => void,
}) {
  const iconPaths = {
    grid: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
    chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  }

  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors
        ${active
          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
          : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
        }
      `}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[icon as keyof typeof iconPaths]} />
      </svg>
      <span className="flex-1 font-medium">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
          {badge}
        </span>
      )}
    </button>
  )
}
